import { Cuepoint, IRoughNotation } from "../../binder/types";
import {
  transformKeysToCamelCase,
  transformKeysToSnakeCase,
} from "pages/media/utils";
import { ExtendedSectionHighlight } from "../types";
import {
  HTMLHighlightsSource,
  PDFHighlightsSource,
  SectionCuepoints,
  SectionHighlight,
  SectionHighlights,
  Storyboard,
  StoryboardHighlights,
} from "pages/media/types";
import React from "react";
import {
  Highlights,
  HtmlHighlight,
  PdfHighlight,
} from "../../animator/useHighlightsStore";
import { MediaItem } from "../../storyboardEditor/types";
import { getWordSpans } from "pages/media/helpers/scriptAnchors/";

export const defaultBrackets: IRoughNotation["brackets"] = ["left", "right"];
export const defaultRoughNotation: IRoughNotation = {
  animate: true,
  type: "highlight",
  animationDelay: 500,
  animationDuration: 800,
  strokeWidth: 4,
  padding: 5,
  color: "#1677FF",
  brackets: [],
  iterations: 2,
  multiline: true,
};

export const loadProjectAsset = async (path: string) => {
  try {
    if (path) {
      const response = await fetch(path);
      const json = await response.json();
      return json;
    }
  } catch (error) {
    console.error("Issue while loading JSON:", error);
  }
};

export const extractSourcesHighlightsCuepoints = (
  storyboardHighlights?: StoryboardHighlights | null
) => {
  const highlights: Highlights = [];
  const cuepoints: Cuepoint[] = [];
  const sources: ExtendedSectionHighlight[] = [];

  if (!storyboardHighlights) return;

  Object.entries(storyboardHighlights?.section_highlights).forEach(
    ([key, item]) => {
      const highlightedScriptWithSectionId: ExtendedSectionHighlight[] =
        item.highlights.map((h: SectionHighlight) => ({
          ...transformKeysToCamelCase(h),
          sectionId: key,
        }));

      item.sources.forEach((sourceObject) => {
        const source = highlightedScriptWithSectionId.find(
          (source) => source.sourceId === sourceObject.id
        );
        const cuepointIndex = item.cuepoints?.findIndex(
          (c) => c.highlight_id === source?.id
        );

        let highlight: PdfHighlight | HtmlHighlight;

        if (sourceObject.type === "pdf") {
          const position = {
            ...transformKeysToCamelCase(sourceObject.zone.position),
            usePdfCoordinates: sourceObject.generated,
          };
          highlight = {
            type: "pdf",
            ...transformKeysToCamelCase(sourceObject),
            content: sourceObject.comment,
            position,
          };
        } else if (sourceObject.type === "html") {
          highlight = {
            type: "html",
            ...transformKeysToCamelCase(sourceObject),
            selectors: sourceObject.selectors,
            position: {
              usePdfCoordinates: sourceObject.generated,
            },
          };
        }

        if (!highlight) return;
        highlights.push(highlight);

        if (cuepointIndex !== -1) {
          const cuepoint = transformKeysToCamelCase(
            item.cuepoints[cuepointIndex]
          );

          cuepoints.push({
            ...cuepoint,
            start: cuepoint.start / 1000,
            duration: cuepoint?.duration / 1000,
            roughNotation: transformKeysToCamelCase(
              sourceObject.rough_notation
            ),
            sourceId: sourceObject.id,
          });
        }
      });
      sources.push(...highlightedScriptWithSectionId);
    }
  );

  const sortedCuepoints = sortCuepointsByStartAscending(cuepoints);
  const sortedHighlights = sortHighlightsByCuepoints(
    highlights,
    sortedCuepoints
  );

  return { sources, highlights: sortedHighlights, cuepoints: sortedCuepoints };
};

const buildSectionHighlights = (
  sectionId: string,
  sources: ExtendedSectionHighlight[],
  highlights: Highlights,
  cuepoints: Cuepoint[],
  oldSection?: SectionHighlights[string]
): SectionHighlights[string] => {
  const htmlSectionHighlights: HTMLHighlightsSource[] = [];
  const pdfSectionHighlights: PDFHighlightsSource[] = [];
  const sectionHighlights: SectionHighlight[] = [];
  const sectionCuepoints: SectionCuepoints[] = [];

  const currentSectionSources = sources
    ?.filter((s) => s.sectionId === sectionId)
    ?.map((s) => s.sourceId);

  const isHtml = highlights.every((h) => "selectors" in h);

  highlights
    ?.filter((h) => currentSectionSources.includes(h.id))
    ?.forEach((h) => {
      const oldSource = oldSection?.sources?.find((s) => s.id === h.id);
      const newSource = sources?.find((s) => s.sourceId === h.id);
      const newCuepoint = cuepoints?.find((c) => c.sourceId === h.id);

      const baseHighlight = {
        id: h?.id,
        generated: oldSource?.generated ?? false,
        rough_notation: transformKeysToSnakeCase(
          newCuepoint?.roughNotation ?? defaultRoughNotation
        ),
      };

      const isHtmlHighlight = "selectors" in h;

      // Preserve every field of the original source loaded from the asset
      // and only override what the user can actually edit in the UI:
      // rough_notation (color/type), zone.position (resize), comment, and
      // selectors (HTML). Anything else (e.g. zone.quote, spans_multiple_lines,
      // content_type, generated) must be round-tripped unchanged — the FE
      // must not rewrite fields it doesn't own.
      if (isHtmlHighlight) {
        if (oldSource) {
          htmlSectionHighlights.push({
            ...(oldSource as HTMLHighlightsSource),
            rough_notation: baseHighlight.rough_notation,
            selectors: h.selectors,
          });
        } else {
          htmlSectionHighlights.push({
            ...baseHighlight,
            type: "html",
            content_type: "image",
            selectors: h.selectors,
          });
        }
      } else {
        if (oldSource) {
          const oldPdfSource = oldSource as PDFHighlightsSource;
          pdfSectionHighlights.push({
            ...oldPdfSource,
            rough_notation: baseHighlight.rough_notation,
            zone: {
              ...oldPdfSource.zone,
              position: transformKeysToSnakeCase(h?.position),
            },
            comment: h?.comment,
          });
        } else {
          // Freshly drawn highlight: seed zone.quote from the PDF highlighter's
          // extracted text (react-pdf-highlighter stores it in content.text).
          const pdfHighlightContent = h as { content?: { text?: string } };
          pdfSectionHighlights.push({
            ...baseHighlight,
            type: "pdf",
            zone: {
              quote: pdfHighlightContent?.content?.text ?? "",
              position: transformKeysToSnakeCase(h?.position),
              spans_multiple_lines: true,
            },
            comment: h?.comment,
          });
        }
      }

      const { sectionId, ...source } = newSource;
      sectionHighlights.push({
        ...transformKeysToSnakeCase(source),
      });

      if (newCuepoint) {
        const start = Math.round((newCuepoint?.start ?? 0) * 1000);
        sectionCuepoints.push({
          highlight_id: source?.id,
          start,
          duration: (newCuepoint?.duration ?? 1) * 1000,
          generated: false,
          relative_start: start,
        });
      }
    });

  const commonProperties = {
    highlights: sectionHighlights,
    cuepoints: sectionCuepoints,
  };

  if (isHtml) {
    return {
      sources: htmlSectionHighlights,
      ...commonProperties,
    };
  }
  return {
    sources: pdfSectionHighlights,
    ...commonProperties,
  };
};

export const reconstructStoryboardHighlights = (
  sources: ExtendedSectionHighlight[],
  highlights: Highlights,
  cuepoints: Cuepoint[],
  originalStoryboard: StoryboardHighlights,
  storyboard: Storyboard
): StoryboardHighlights | undefined => {
  if (!cuepoints || !highlights || !sources) return;

  const section_highlights: StoryboardHighlights["section_highlights"] = {};

  const newSectionMap = {};
  storyboard?.sections.forEach((section) => {
    newSectionMap[section?.id] = section.topic;
  });
  if (storyboard) {
    Object.keys(newSectionMap).forEach((sectionId) => {
      const oldSection = originalStoryboard?.section_highlights[sectionId];
      section_highlights[sectionId] = buildSectionHighlights(
        sectionId,
        sources,
        highlights,
        cuepoints,
        oldSection
      );
    });
  } else {
    Object.keys(originalStoryboard?.section_highlights).forEach((sectionId) => {
      section_highlights[sectionId] =
        originalStoryboard?.section_highlights[sectionId];
    });
  }
  return {
    document_id: originalStoryboard.document_id,
    section_map: newSectionMap,
    section_highlights,
    section_timestamps: originalStoryboard?.section_timestamps,
  } as StoryboardHighlights;
};

export const reconstructStoryboardHighlightsWithoutStoryboard = (
  sources: ExtendedSectionHighlight[],
  highlights: Highlights,
  cuepoints: Cuepoint[],
  document_id: string,
  sectionTimestamps?: StoryboardHighlights["section_timestamps"]
): StoryboardHighlights | undefined => {
  if (!cuepoints || !highlights || !sources || !document_id) return;

  const section_map = { no_storyboard: "no_storyboard" };
  const section_highlights = {
    no_storyboard: buildSectionHighlights(
      "no_storyboard",
      sources,
      highlights,
      cuepoints
    ),
  };

  return {
    document_id,
    section_map,
    section_highlights,
    section_timestamps: sectionTimestamps,
  } as StoryboardHighlights;
};

export const registerHighlightsRef = (
  ref: React.RefObject<Record<string, HTMLElement>>,
  id: string,
  element: HTMLElement
) => {
  if (!ref.current || ref.current?.[id]) return;
  ref.current[id] = element;
};

export const scrollToHighlight = (
  ref: React.RefObject<Record<string, HTMLElement>>,
  id: string
) => {
  if (!ref.current) return;
  const element = ref.current[id];

  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }
};

export const getHandleDirection = (
  isRTL: boolean,
  activeHandle: "start" | "end"
) => {
  if (activeHandle === "start" && isRTL) return "end";
  if (activeHandle === "end" && isRTL) return "start";
  return activeHandle;
};

export const sortCuepointsByStartAscending = (
  cuepoints: Cuepoint[]
): Cuepoint[] => {
  if (cuepoints.length === 0) return cuepoints;
  return cuepoints.toSorted((a, b) => a.start - b.start);
};

export const sortHighlightsByCuepoints = (
  highlights: Highlights,
  cuepoints: Cuepoint[]
): Highlights => {
  if (!cuepoints?.length || !highlights?.length) return highlights;

  const sortedHighlights = cuepoints.reduce((acc: Highlights, cp) => {
    const match = highlights.find(
      (hl) => hl.id === cp.sourceId || hl.id === cp.highlightId
    );
    if (match) acc.push(match);
    return acc;
  }, []);

  let highlightsWithoutCuepoint: Highlights = [];

  if (highlights.length > cuepoints.length) {
    highlightsWithoutCuepoint = highlights.filter(
      (highlight) => !sortedHighlights.includes(highlight)
    );
  }
  return [...sortedHighlights, ...highlightsWithoutCuepoint];
};

export const enrichAssetWithPath = (
  asset: MediaItem,
  existingAssets: MediaItem[]
): MediaItem => {
  const existing = existingAssets?.find(
    (item) => item.assetId === asset.assetId
  );
  return { ...asset, path: existing?.path };
};

/**
 * Map a character position to the word it falls in (or the next word when it
 * falls between words). Words can touch in Chinese, so a word's start must not
 * resolve to the previous word's end.
 */
export const charPosToWordIndex = (
  text: string,
  charPos: number,
  language?: string
): number => {
  const spans = getWordSpans(text, language);
  const index = spans.findIndex((span) => charPos < span.end);
  return index === -1 ? spans.length : index;
};

/** Map a word index to the character position (start) of that word. */
export const wordIndexToCharPos = (
  text: string,
  wordIndex: number,
  language?: string
): number => {
  const spans = getWordSpans(text, language);
  return wordIndex < spans.length ? spans[wordIndex].start : text.length;
};

/** Map a word index to the character position (end) of that word. */
export const wordIndexToCharEnd = (
  text: string,
  wordIndex: number,
  language?: string
): number => {
  const spans = getWordSpans(text, language);
  return wordIndex < spans.length ? spans[wordIndex].end : text.length;
};
